import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep } from 'lodash/fp';
import { showModal } from '@store/actions/commonAction';
import DiseaseHistory from '@components/ea/DiseaseHistory';
import AddSubscription from '@components/ea/AddSubscription';
import SatToolTip from '@components/common/satUI/SatToolTip';
import SatQuestionArea from '@components/common/satUI/SatQuestionArea';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatSelect from '@components/common/satUI/SatSelect';
import SatButton from '@components/common/satUI/SatButton';
import {
  checkProduct,
  diseaseTreatInfo,
  isEmptyString,
  updateObject,
} from '@common/utils';
import { DISEASE_ETC_CODE, DISEASE_INIT_DATA } from '@common/constants';

const DiseaseInfo = props => {
  const {
    genderLabel,
    category1,
    onChange,
    onNext,
    isDisabled,
    setIsDisabled,
    isEasyProduct,
    isMedicalProduct,
    isFreePickProduct,
    isSecondMajorRider,
    isDementiaProduct,
    planEstablishProduct,
    isAddNoticeQ6,
    isBeforeRevision,
  } = props;

  const dispatch = useDispatch();

  const [selectedOption, setSelectedOption] = useState('');
  const [errorCheckButton, setErrorCheckButton] = useState([]); // 예아니오 validation check
  const [errorCheckSubData, setErrorCheckSubData] = useState([]); // 세부항목 validation check
  const [showErrorCheckMsg, setShowErrorCheckMsg] = useState(false);
  const [showDiseaseHistory, setShowDiseaseHistory] = useState(false); // 병력정보 노출 여부
  const [showAddSubscription, setShowAddSubscription] = useState(false); // 추가신청서 노출 여부
  const [isSpecificConstant, setIsSpecificConstant] = useState(false); // 특별인수특약 check 고정 여부
  const [riderContract, setRiderContract] = useState(''); // 특별인수특약 check 고정을 위한 특약상품리스트

  const pregnantMonthList = [
    { label: '1개월', value: '1' },
    { label: '2개월', value: '2' },
    { label: '3개월', value: '3' },
    { label: '4개월', value: '4' },
    { label: '5개월', value: '5' },
    { label: '6개월', value: '6' },
    { label: '7개월', value: '7' },
    { label: '8개월', value: '8' },
    { label: '9개월', value: '9' },
    { label: '10개월', value: '10' },
  ];

  const toolTips = {
    requireAddCheck: {
      title: '추가검사(재검사)에 대한 정의',
      message:
        '검사 결과 이상 소견이 확인되어 보다 정확한 진단을 위해 시행한 검사를 의미하며, 병증에 대한 치료 필요 없이 유지되는 상태에서 시행하는 정기검사 또는 추적관찰은 포함하지 않습니다.',
    },
    requireDoubtCheck: {
      title: '필요 또는 의심소견',
      message:
        '필요 또는 의심소견이란 의사가 진단서나 소견서 또는 진료의뢰서 등을 포함하여 서면(전자문서 포함)으로 교부한 경우를 말합니다.',
    },
    requireHospital: {
      title: '필요 또는 의심소견',
      message:
        '필요 또는 의심소견이란 의사가 진단서나 소견서 또는 진료의뢰서 등을 포함하여 서면(전자문서 포함)으로 교부한 경우를 말합니다.',
    },
    requireOperation: {
      title: '필요소견',
      message:
        '필요소견이란 의사가 진단서, 소견서 등에 기재한 경우를 말합니다.',
    },
    cancer: {
      title: '암',
      message: '암에는 악성신생물, 백혈병 및 기타 혈액종양이 포함됩니다. ',
    },
    mentalnoteDisease: {
      title: '질병의심소견',
      message:
        '질병의심소견이란 의사가 진단서나 소견서 또는 진료의뢰서 등을 포함하여 서면(전자문서 포함)으로 교부한 경우를 말합니다.',
    },
    dosing: {
      title: '투약',
      message:
        '투약이란 의사가 환자에게 약을 처방하는 행위를 말하는 것으로 실제로 약을 구입하지 않았어도 기재해야 합니다.',
    },
    bpLowering: {
      title: '혈압강하제',
      message: '혈압을 내리게 하는 의약품을 말합니다.',
    },
    uppers: {
      title: '각성제(흥분제)',
      message: '신경계를 흥분시켜 잠이 오는 것을 억제하는 의약품을 말합니다.',
    },
    continue: {
      title: '계속하여',
      message:
        '여기서 계속하여란 같은 원인으로 치료 시작후 완료일까지 실제 치료, 투약받은 일수를 말합니다.',
    },
    addNotice: {
      title: '추가 고지사항',
      message: '병력정보가 3개를 초과하는 경우 체크해주세요.',
    },
    treatment7: {
      title: '계속하여',
      message:
        '여기서 계속하여란 같은 원인으로 치료 시작후 완료일까지 실제 치료, 투약받은 일수를 말합니다.',
    },
  };

  const mediKindsFirst = isMedicalProduct
    ? [
        { label: '입원', value: 'hospitalization' },
        { label: '수술', value: 'operation' },
        { label: '계속하여 7일 이상 치료', value: 'treatment7', tooltip: true },
        { label: '계속하여 30일 이상 투약', value: 'dosing30' },
      ]
    : !isEasyProduct
    ? [
        { label: '질병확정진단', value: 'confirmDiagnosis' },
        { label: '질병의심소견', value: 'mentalnoteDisease', tooltip: true },
        { label: '치료', value: 'treatment' },
        { label: '입원', value: 'hospitalization' },
        { label: ( !isDementiaProduct ? '수술(제왕절개 포함)' : '수술'), value: 'operation' },
        { label: '투약', value: 'dosing', tooltip: true },
      ]
    : [
        { label: '질병확정진단', value: 'requireConfirmCheck' },
        { label: '질병의심소견', value: 'requireDoubtCheck', tooltip: true },
        { label: '추가검사(재검사)필요소견', value: 'requireAddCheck', tooltip: true },
        { label: '입원필요소견', value: 'requireHospital', tooltip: true },
        { label: '수술필요소견', value: 'requireOperation' },
      ];

  const mediKindsSecond = !isDementiaProduct ? [
    { label: '입원', value: 'hospitalization' },
    { label: '수술(제왕절개 포함)', value: 'operation' },
  ]  : [
    { label: '입원', value: 'hospitalization' },
    { label: '수술', value: 'operation' },
    { label: '계속하여 7일 이상 치료', value: 'treatment7', tooltip: true },
    { label: '계속하여 30일 이상 투약', value: 'dosing30' },
  ];

  const mediKindsFourth = [
    { label: '입원', value: 'hospitalization' },
    { label: '수술(제왕절개 포함)', value: 'operation' },
    { label: '계속하여 7일 이상 치료', value: 'treatment7', tooltip: true },
    { label: '계속하여 30일 이상 투약', value: 'dosing30' },
  ];

  const mediKindsFifth = {
    bestDisease: [
      { label: '암', value: 'cancer' },
      { label: '백혈병', value: 'leukemia' },
      { label: '고혈압', value: 'highBloodPressure' },
      { label: '협심증', value: 'hypophobia' },
      { label: '심근경색', value: 'myocardial' },
      { label: '심장판막증', value: 'cardiomyopathy' },
      { label: '간경화증', value: 'hepaticSclerosis' },
      { label: '뇌졸중증(뇌출혈, 뇌경색)', value: 'stroke' },
      { label: '당뇨', value: 'diabetes' },
      { label: '에이즈(AIDS) 및 HIV 보균', value: 'aids' },
    ],
    checkList: [
      { label: '질병확정진단', value: 'diagnosis' },
      { label: '치료', value: 'treatment' },
      { label: '입원', value: 'hospitalization' },
      { label: '수술', value: 'operation' },
      { label: '투약', value: 'dosing' },
    ],
  };

  const dementiaThird = {
    bestDisease: [
      { label: '뇌졸중증', value: 'stroke' },
      { label: '치매', value: 'dementia' },
      { label: '알츠하이머', value: 'alzheimer' },
      { label: '파킨슨병', value: 'parkinson' },
      { label: '외상성뇌손상', value: 'traumaticBrainInjury' },
    ],
    checkList: [
      { label: '질병확정진단', value: 'confirmDiagnosis' },
      { label: '치료', value: 'treatment' },
      { label: '입원', value: 'hospitalization' },
      { label: '수술', value: 'operation' },
      { label: '투약', value: 'dosing' },
    ],
  };

  const adjustInitData = () => {
    // 병력 정보가 있으면, 초기값 빈 값들 설정
    const diseaseInfos = category1?.q07 ?? [];

    if (diseaseInfos.length > 0) {
      const adjustedDiseaseInfos = diseaseInfos.map(diseaseInfo => {
        const newDiseaseInfo = cloneDeep(DISEASE_INIT_DATA);
        const mergedDiseaseInfo = updateObject(newDiseaseInfo, diseaseInfo);

        // 이상한 데이터들 보정
        // diagnosis가 빈 값이 아닐 때 [kcdCd, kcdNm] 확인
        const { diagnosis, kcdCd, kcdNm, treatStrtDate, treatEndDate } =
          mergedDiseaseInfo;
        if (!isEmptyString(diagnosis)) {
          if (isEmptyString(kcdCd)) mergedDiseaseInfo.kcdCd = DISEASE_ETC_CODE;
          if (isEmptyString(kcdNm)) mergedDiseaseInfo.kcdNm = diagnosis;
        }

        // treatStrtDate/treatEndDate/treatDayCount 확인
        const treatStart = treatStrtDate?.replace(/[^0-9]/g, '');
        const treatEnd = treatEndDate?.replace(/[^0-9]/g, '');
        mergedDiseaseInfo.treatStrtDate = treatStart;
        mergedDiseaseInfo.treatEndDate = treatEnd;
        const { treatDayCount, isValidDate } = diseaseTreatInfo(
          treatStart,
          treatEnd,
        );
        if (isValidDate) {
          mergedDiseaseInfo.treatDayCount = treatDayCount;
        }

        return mergedDiseaseInfo;
      });

      onChange({
        category1: {
          ...category1,
          q07: adjustedDiseaseInfos,
        },
      });
    }
  };

  useEffect(() => {
    checkButtonVisible();

    const findData = planEstablishProduct.find(
      item =>
        item.mainPolicyRiderTypeCode === 'R' &&
        checkProduct(item, 'speciticCodeList'),
    );

    if (findData && category1?.q06?.checkYn === 'Y') {
      setIsSpecificConstant(true);
      handleAddSubscriptionCheckBox('specific', true);
    }

    setRiderContract(findData);

    // 초기 데이터 보정
    adjustInitData();
  }, []);

  useEffect(() => {
    // 병력정보 체크
    if (!isEasyProduct) {
      const checkYn = getCheckYn();
      if (checkYn.length > 0) {
        if (checkYn.length === 1 && checkYn[0] === 'q06') {
          // 임신에 예 체크
          setShowDiseaseHistory(false); // 병력정보 노출되지 않도록 예외처리
          setShowAddSubscription(true); // 추가신청서는 노출
        } else {
          // 예 체크가 있으면 병력정보 기재 영역 노출
          if (category1.q07.length === 0) {
            onChange({
              category1: {
                ...category1,
                q07: [DISEASE_INIT_DATA],
              },
            });
          }
          setShowDiseaseHistory(true);
          setShowAddSubscription(true);
        }
      } else {
        setShowDiseaseHistory(false);
      }
    }

    checkButtonVisible();
  }, [category1]);

  const getCheckYn = () => {
    let checkYn = [];
    if (isDementiaProduct) {
      // 치매상품
      for (const key of Object.keys(category1)) {
        if (key !== 'q04') {
          if (category1[key].checkYn === 'Y') {
            // checkYn = true;
            checkYn.push(key);
            // break;
          }
        } else {
          if (category1[key] === 'Y') {
            checkYn.push(key);
            // break;
          }
        }
      }
    } else if (!isEasyProduct) {
      // 일반상품
      for (const key of Object.keys(category1)) {
        if (key !== 'q02' && key !== 'q03') {
          if (category1[key].checkYn === 'Y') {
            // checkYn = true;
            checkYn.push(key);
            // break;
          }
        } else {
          if (category1[key] === 'Y') {
            checkYn.push(key);
            // break;
          }
        }
      }
    } else {
      // 간편상품
      for (const key of Object.keys(category1)) {
        if (key === 'q01' || key === 'q02') {
          if (category1[key].checkYn === 'Y') {
            checkYn.push(key);
            // break;
          }
        } else if (key === 'q03') {
          if (category1[key] === 'Y') {
            checkYn.push(key);
            // break;
          }
        }
      }
    }

    return checkYn;
  };

  const addDiseaseHistory = () => {
    let resultData = category1.q07.map(item => {
      return item;
    });
    resultData.push(DISEASE_INIT_DATA);
    onChange({
      category1: {
        ...category1,
        q07: resultData,
      },
    });
  };

  const validationButton = checkData => {
    // 예 아니오 미선택 check
    const checkArr = [];
    if (isDementiaProduct) {
      for (const key of Object.keys(checkData)) {
        if (key !== 'q04') {
          if (checkData[key].checkYn === '') {
            checkArr.push(key);
          }
        } else if (checkData[key] === '') {
          checkArr.push(key);
        }
      }
    } else if (!isEasyProduct) {
      for (const key of Object.keys(checkData)) {
        if (key !== 'q02' && key !== 'q03') {
          if (checkData[key].checkYn === '') {
            checkArr.push(key);
          }
        } else if (checkData[key] === '') {
          checkArr.push(key);
        }
      }
    } else {
      // 간편상품
      for (const key of Object.keys(checkData)) {
        if (key === 'q01' || key === 'q02') {
          if (checkData[key].checkYn === '') {
            checkArr.push(key);
          }
        } else if (key === 'q03') {
          if (checkData[key] === '') {
            checkArr.push(key);
          }
        }
      }
    }

    if (checkArr.length > 0) {
      setErrorCheckButton(checkArr);
    } else {
      setErrorCheckButton([]);
    }
  };

  const validationCheckBox = checkData => {
    // 예를 선택하고 세부항목 미선택 check
    const checkArr = [];

    // 치매보험 상품
    if (isDementiaProduct) {
      const diseaseList = [
        'stroke', // 노졸중증
        'dementia', // 치매
        'alzheimer', // 알츠하이머
        'parkinson', // 파킨슨병
        'traumaticBrainInjury', // 외상성뇌손상
      ];
      const treatmentType = [
        'confirmDiagnosis', // 질병확정진단
        'treatment', // 치료
        'hospitalization', // 입원
        'operation', // 수술
        'dosing', // 투약
      ];

      for (const key of Object.keys(checkData)) {
        if (key !== 'q04') {
          if (checkData[key].checkYn === 'Y') {
            // 예 항목에 체크하였으나 체크박스 미체크 항목 추출
            let checkYn = false;
            let diseaseListCheck = false;
            let treatmentTypeCheck = false;
            for (const fieldName of Object.keys(checkData[key])) {
              if (fieldName !== 'checkYn') {
                if (key === 'q03') {
                  if (
                    diseaseList.includes(fieldName) &&
                    checkData[key][fieldName] === 'Y'
                  ) {
                    diseaseListCheck = true;
                  } else if (
                    treatmentType.includes(fieldName) &&
                    checkData[key][fieldName] === 'Y'
                  ) {
                    treatmentTypeCheck = true;
                  }
                } else {
                  if (checkData[key][fieldName] === 'Y') {
                    checkYn = true;
                  }
                }
              }
            }
            if (!checkYn) {
              if (key === 'q03') {
                if (!diseaseListCheck || !treatmentTypeCheck) {
                  checkArr.push(key);
                }
              } else {
                checkArr.push(key);
              }
            }
          }
        }
      }
      // 일반상품
    } else if (!isEasyProduct) {
      const diseaseList = [
        'cancer',
        'leukemia',
        'highBloodPressure',
        'hypophobia',
        'myocardial',
        'cardiomyopathy',
        'hepaticSclerosis',
        'stroke',
        'diabetes',
        'aids',
      ];
      const treatmentType = [
        'diagnosis',
        'treatment',
        'hospitalization',
        'operation',
        'dosing',
      ];

      for (const key of Object.keys(checkData)) {
        if (key !== 'q02' && key !== 'q03') {
          if (checkData[key].checkYn === 'Y') {
            // 예 항목에 체크하였으나 체크박스 미체크 항목 추출
            let checkYn = false;
            let diseaseListCheck = false;
            let treatmentTypeCheck = false;
            for (const fieldName of Object.keys(checkData[key])) {
              if (fieldName !== 'checkYn') {
                if (key === 'q05') {
                  if (
                    diseaseList.includes(fieldName) &&
                    checkData[key][fieldName] === 'Y'
                  ) {
                    diseaseListCheck = true;
                  } else if (
                    treatmentType.includes(fieldName) &&
                    checkData[key][fieldName] === 'Y'
                  ) {
                    treatmentTypeCheck = true;
                  }
                } else {
                  if (
                    checkData[key][fieldName] === 'Y' ||
                    (key === 'q06' && checkData[key][fieldName] !== '')
                  ) {
                    checkYn = true;
                  }
                }
              }
            }
            if (!checkYn) {
              if (key === 'q05') {
                if (!diseaseListCheck || !treatmentTypeCheck) {
                  checkArr.push(key);
                }
              } else {
                checkArr.push(key);
              }
            }
          }
        }
      }
    } else {
      // 간편상품
      for (const key of Object.keys(checkData)) {
        if (key === 'q01' || key === 'q02') {
          if (checkData[key].checkYn === 'Y') {
            // 예 항목에 체크하였으나 체크박스 미체크 항목 추출
            let checkYn = false;
            for (const fieldName of Object.keys(checkData[key])) {
              if (fieldName !== 'checkYn') {
                if (checkData[key][fieldName] === 'Y') {
                  checkYn = true;
                }
              }
            }
            if (!checkYn) {
              checkArr.push(key);
            }
          }
        }
      }
    }

    if (checkArr.length > 0) {
      setErrorCheckSubData(checkArr);
    } else {
      setErrorCheckSubData([]);
    }
  };

  const checkButtonVisible = () => {
    if (!isAddNoticeQ6 && genderLabel === '여') {
      if (!isEasyProduct) {
        delete category1.q06;
      }
    }
    if (isMedicalProduct) {
      delete category1.q02;
      delete category1.q03;
      delete category1.q04;
      delete category1.q05;
    }
    // 하단 다음 버튼 visible 처리
    let disabledYn = false;
    const checkYn = getCheckYn();

    if (isDementiaProduct) {
      outerfor: for (const key of Object.keys(category1)) {
        // 예 항목에 체크하였으나 체크박스 미체크 항목 확인
        if (key !== 'q04') {
          if (category1[key].checkYn === 'Y') {
            let checkBoxYn = false;
            let diseaseListCheck = false;
            let treatmentTypeCheck = false;
            for (const fieldName of Object.keys(category1[key])) {
              if (fieldName !== 'checkYn') {
                if (key === 'q03') {
                  const diseaseList =[
                    'stroke', // 노졸중증
                    'dementia', // 치매
                    'alzheimer', // 알츠하이머
                    'parkinson', // 파킨슨병
                    'traumaticBrainInjury', // 외상성뇌손상
                  ];
                  const treatmentType = [
                    'confirmDiagnosis', // 질병확정진단
                    'treatment', // 치료
                    'hospitalization', // 입원
                    'operation', // 수술
                    'dosing', // 투약
                  ];

                  if (
                    diseaseList.includes(fieldName) &&
                    category1[key][fieldName] === 'Y'
                  ) {
                    diseaseListCheck = true;
                  } else if (
                    treatmentType.includes(fieldName) &&
                    category1[key][fieldName] === 'Y'
                  ) {
                    treatmentTypeCheck = true;
                  }

                  if (diseaseListCheck && treatmentTypeCheck) checkBoxYn = true;
                } else if (category1[key][fieldName] === 'Y') {
                  checkBoxYn = true;
                }
              }
            }
            if (!checkBoxYn) {
              disabledYn = true;
              break outerfor;
            }
          }
        } else {
          if (category1[key] === '') {
            disabledYn = true;
            break outerfor;
          }
        }

        // 병력정보 확인
        if (checkYn.length > 0 && key === 'q07') {
          for (const subkey of Object.keys(category1.q07)) {
            for (const fieldName of Object.keys(category1.q07[subkey])) {
              if (!isBeforeRevision && fieldName === 'hospital'){
                // 2025.01 부터는 병원명을 체크 안함.
              } else if (
                fieldName === 'hospitalization' ||
                fieldName === 'operation' ||
                fieldName === 'outpatient'
              ) {
                if (
                  (category1.q07[subkey].hospitalization === '' ||
                    category1.q07[subkey].hospitalization === '0') &&
                  (category1.q07[subkey].operation === '' ||
                    category1.q07[subkey].operation === '0') &&
                  (category1.q07[subkey].outpatient === '' ||
                    category1.q07[subkey].outpatient === '0')
                ) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (fieldName === 'treatArea') {
                if (
                  !category1.q07[subkey].treatArea.left &&
                  !category1.q07[subkey].treatArea.right &&
                  !category1.q07[subkey].treatArea.na
                ) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (
                fieldName === 'treatStrtDate' ||
                fieldName === 'treatEndDate'
              ) {
                if (category1.q07[subkey][fieldName]?.length < 8) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (category1.q07[subkey][fieldName] === '') {
                disabledYn = true;
                break outerfor;
              }
            }
          }
        }
      }
    } else if (!isEasyProduct) {
      outerfor: for (const key of Object.keys(category1)) {
        if (category1[key].checkYn === '') {
          // 예/아니오 미선택항목 확인
          if (key === 'q06' && genderLabel === '남') {
            continue;
          }
          disabledYn = true;
          break outerfor;
        } else {
          // 예 항목에 체크하였으나 체크박스 미체크 항목 확인
          if (key !== 'q02' && key !== 'q03') {
            if (category1[key].checkYn === 'Y') {
              let checkBoxYn = false;
              let diseaseListCheck = false;
              let treatmentTypeCheck = false;
              for (const fieldName of Object.keys(category1[key])) {
                if (fieldName !== 'checkYn') {
                  if (key === 'q05') {
                    const diseaseList = [
                      'cancer',
                      'leukemia',
                      'highBloodPressure',
                      'hypophobia',
                      'myocardial',
                      'cardiomyopathy',
                      'hepaticSclerosis',
                      'stroke',
                      'diabetes',
                      'aids',
                    ];
                    const treatmentType = [
                      'diagnosis',
                      'treatment',
                      'hospitalization',
                      'operation',
                      'dosing',
                    ];

                    if (
                      diseaseList.includes(fieldName) &&
                      category1[key][fieldName] === 'Y'
                    ) {
                      diseaseListCheck = true;
                    } else if (
                      treatmentType.includes(fieldName) &&
                      category1[key][fieldName] === 'Y'
                    ) {
                      treatmentTypeCheck = true;
                    }

                    if (diseaseListCheck && treatmentTypeCheck)
                      checkBoxYn = true;
                  } else if (
                    category1[key][fieldName] === 'Y' ||
                    (key === 'q06' && category1[key][fieldName] !== '')
                  ) {
                    checkBoxYn = true;
                  }
                }
              }
              if (!checkBoxYn) {
                disabledYn = true;
                break outerfor;
              }
            }
          } else {
            if (category1[key] === '') {
              disabledYn = true;
              break outerfor;
            }
          }
        }

        // 병력정보 확인
        if (checkYn.length > 0 && key === 'q07') {
          for (const subkey of Object.keys(category1.q07)) {
            for (const fieldName of Object.keys(category1.q07[subkey])) {
              if (!isBeforeRevision && fieldName === 'hospital') {
                // 2025.01 부터는 병원명을 체크 안함.
              } else if (
                fieldName === 'hospitalization' ||
                fieldName === 'operation' ||
                fieldName === 'outpatient'
              ) {
                if (
                  (category1.q07[subkey].hospitalization === '' ||
                    category1.q07[subkey].hospitalization === '0') &&
                  (category1.q07[subkey].operation === '' ||
                    category1.q07[subkey].operation === '0') &&
                  (category1.q07[subkey].outpatient === '' ||
                    category1.q07[subkey].outpatient === '0')
                ) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (fieldName === 'treatArea') {
                if (
                  !category1.q07[subkey].treatArea.left &&
                  !category1.q07[subkey].treatArea.right &&
                  !category1.q07[subkey].treatArea.na
                ) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (
                fieldName === 'treatStrtDate' ||
                fieldName === 'treatEndDate'
              ) {
                if (category1.q07[subkey][fieldName]?.length < 8) {
                  disabledYn = true;
                  break outerfor;
                }
              } else if (category1.q07[subkey][fieldName] === '') {
                disabledYn = true;
                break outerfor;
              }
            }
          }
        }
      }
    } else {
      // 간편상품
      outerfor: for (const key of Object.keys(category1)) {
        if (key === 'q01' || key === 'q02' || key === 'q03') {
          if (key === 'q01' || key === 'q02') {
            if (category1[key].checkYn === '') {
              // 예아니오 미선택항목 확인
              disabledYn = true;
              break outerfor;
            } else if (category1[key].checkYn === 'Y') {
              // 질병항목에 하나라도 예를 체크한 경우 심사 진행 불가 SAT-2624
              disabledYn = true;
              break outerfor;
            }
          } else if (key === 'q03') {
            if (category1[key] === '') {
              disabledYn = true;
              break outerfor;
            } else if (category1[key] === 'Y') {
              // 질병항목에 하나라도 예를 체크한 경우 심사 진행 불가 SAT-2624
              disabledYn = true;
              break outerfor;
            }
          }
        }
      }
    }

    setIsDisabled(disabledYn);
  };

  // 예, 아니오 버튼
  const handleButtonYnClick = (type, value) => {
    // 치매보험 상품
    if (isDementiaProduct) {
      if (value === 'N' && type !== 'q04') {
        // 아니오 버튼 선택 시 하위 요소 모두 해제
        for (const key of Object.keys(category1[type])) {
          category1[type][key] = '';
        }
      }

      let changeData = {
        ...category1,
        [type]:
          type !== 'q04'
            ? {
                ...category1[type],
                checkYn: value,
              }
            : value,
      };

      onChange({
        category1: changeData,
      });

      validationCheckBox(category1);

      let isCheckY = false;
      for (const key of Object.keys(changeData)) {
        if (key !== 'q04') {
          if (changeData[key].checkYn === 'Y') {
            // 예 항목에 체크하였으나 체크박스 미체크 항목 추출
            isCheckY = true;
            break;
          }
        } else {
          if (changeData[key] === 'Y') {
            isCheckY = true;
            break;
          }
        }
      }
      if (!isCheckY) {
        // 모두 아니오 선택 시 병력정보와 추가신청서 초기화
        changeData = {
          ...changeData,
          q07: [],
          q08: {
            decrement: false,
            specific: false,
          },
        };

        setShowDiseaseHistory(false);
        setShowAddSubscription(false);
      }

      onChange({
        category1: changeData,
      });

      validationButton(changeData);

      // 일반상품.
    } else if (!isEasyProduct) {
      if (value === 'N' && type !== 'q02' && type !== 'q03') {
        // 아니오 버튼 선택 시 하위 요소 모두 해제
        for (const key of Object.keys(category1[type])) {
          category1[type][key] = '';
        }
      }

      validationCheckBox(category1);

      let changeData = {
        ...category1,
        [type]:
          type !== 'q02' && type !== 'q03'
            ? {
                ...category1[type],
                checkYn: value,
              }
            : value,
      };

      if (type === 'q06' && riderContract) {
        if (value === 'Y') {
          setIsSpecificConstant(true);
        } else {
          setIsSpecificConstant(false);
        }

        changeData = {
          ...changeData,
          q08: {
            ...changeData.q08,
            specific: value === 'Y' ? true : false,
          },
        };
      }

      let isCheckY = false;
      for (const key of Object.keys(changeData)) {
        if (key !== 'q02' && key !== 'q03') {
          if (changeData[key].checkYn === 'Y') {
            // 예 항목에 체크하였으나 체크박스 미체크 항목 추출
            isCheckY = true;
            break;
          }
        } else {
          if (changeData[key] === 'Y') {
            isCheckY = true;
            break;
          }
        }
      }
      if (!isCheckY) {
        // 모두 아니오 선택 시 병력정보와 추가신청서 초기화
        changeData = {
          ...changeData,
          q07: [],
          q08: {
            decrement: false,
            specific: false,
          },
        };

        setShowDiseaseHistory(false);
        setShowAddSubscription(false);
      }

      onChange({
        category1: changeData,
      });

      validationButton(changeData);
    } else {
      // 간편상품
      if (value === 'N' && type !== 'q03') {
        // 아니오 버튼 선택 시 하위 요소 모두 해제
        for (const key of Object.keys(category1[type])) {
          if (type === 'q01' || type === 'q02') {
            category1[type][key] = '';
          }
        }
      }

      if (value === 'Y') {
        // 간편상품일 경우 예 선택시 가입 진행 불가 안내
        easyProductWarnigMessage(type);
      }

      validationCheckBox(category1);

      const changeData = {
        ...category1,
        [type]:
          type !== 'q03'
            ? {
                ...category1[type],
                checkYn: value,
              }
            : value,
      };
      onChange({
        category1: changeData,
      });

      validationButton(changeData);
    }
  };

  const easyProductWarnigMessage = type => {
    // 간편상품일 경우 예 선택시 가입 진행 불가 안내
    let msg = '';
    if (type === 'q01') {
      msg = `최근 3개월 이내 질병확정진단 및 의심소견, 입원, 수술, 검사 필요소견이 있는 경우 '간편심사상품'의 가입이 불가해요.`;
    } else if (type === 'q02') {
      msg = `최근 2년 이내 입원, 수술 이력이 있는 경우 '간편심사상품'의 가입이 불가해요.`;
    } else if (type === 'q03') {
      msg = `최근 5년 이내 암진단, 암입원, 암수술 이력이 있는 경우 '간편심사상품'의 가입이 불가해요.`;
    }

    dispatch(
      showModal({
        visible: true,
        msg: msg,
      }),
    );
  };

  // 체크박스
  const handleCheckYnClick = (type, fieldName, value) => {
    const changeData = {
      ...category1,
      [type]: {
        ...category1[type],
        [fieldName]: value === true ? 'Y' : 'N',
        checkYn: value === true ? 'Y' : category1[type].checkYn,
      },
    };
    if (isEasyProduct) {
      const checkYn = changeData[type].checkYn;
      const checkYLength = Object.values(changeData[type])
        .map((res, index) => {
          if (index === 0) {
            return '';
          } else {
            return res;
          }
        })
        .filter(res => res === 'Y').length;
      if (checkYn === 'Y' && checkYLength === 1) {
        easyProductWarnigMessage(type);
      }
    }

    onChange({
      category1: changeData,
    });

    validationCheckBox(changeData);
  };

  // 임신개월 수 선택
  const handleSelectPregnant = value => {
    const changeData = {
      ...category1,
      q06: {
        ...category1?.q06,
        pregnancyPeriod: value.value,
        checkYn: value.value !== '' ? 'Y' : category1?.q06?.checkYn,
      },
    };

    onChange({
      category1: changeData,
    });

    validationCheckBox(changeData);
  };

  // 병력정보 > 치료내용 체크
  const handleDiseaseHistoryCheckBox = (findIndex, valueItem) => {
    const resultData = category1.q07.map((item, index) => {
      return findIndex === index ? valueItem : item;
    });
    onChange({
      category1: {
        ...category1,
        q07: resultData,
      },
    });
  };

  // 추가 신청 체크
  const handleAddSubscriptionCheckBox = (fieldName, value) => {
    const changeData = {
      ...category1,
      q08: {
        ...category1.q08,
        [fieldName]: value,
      },
    };

    onChange(
      {
        category1: changeData,
      },
      !value ? fieldName : '',
    );
  };

  // 추가고지사항 체크
  const handleAdditionalNotice = value => {
    const changeData = {
      ...category1,
      q07_2: {
        additionalDisease: value ? 'Y' : 'N',
      },
    };

    onChange({
      category1: changeData,
    });
  };

  // 병력정보 삭제
  const handleDiseaseHistoryDelete = findIndex => {
    const resultData = category1.q07.filter(
      (value, index) => index !== findIndex,
    );
    onChange({
      category1: {
        ...category1,
        q07: resultData,
        q07_2: {
          additionalDisease:
            resultData.length !== 3 ? 'N' : category1.q07_2.additionalDisease,
        },
      },
    });
  };

  // 입력 오류 항목 최상단에 스크롤 위치
  const validationScrollMove = () => {
    const scrollWrapY = document
      .getElementById('notificationScroll')
      .getBoundingClientRect().top; // 스크롤 영역의 y좌표

    const currentScrollTop =
      document.getElementById('notificationScroll').scrollTop;

    if (
      category1.q01.checkYn === '' ||
      (category1.q01.checkYn === 'Y' &&
        category1.q01.confirmDiagnosis === '' &&
        category1.q01.mentalnoteDisease === '' &&
        category1.q01.treatment === '' &&
        category1.q01.hospitalization === '' &&
        category1.q01.operation === '' &&
        category1.q01.dosing === '')
    ) {
      const targetY = document
        .getElementById('q01')
        .getBoundingClientRect().top;

      document.getElementById('notificationScroll').scrollTop =
        currentScrollTop + (targetY - scrollWrapY);
    } else if (!isMedicalProduct) {
      if (category1.q02 === '') {
        const targetY = document
          .getElementById('q02')
          .getBoundingClientRect().top;

        document.getElementById('notificationScroll').scrollTop =
          currentScrollTop + (targetY - scrollWrapY);
      } else if (category1.q03 === '') {
        const targetY = document
          .getElementById('q03')
          .getBoundingClientRect().top;

        document.getElementById('notificationScroll').scrollTop =
          currentScrollTop + (targetY - scrollWrapY);
      }
    } else if (!isEasyProduct) {
      if (
        (!isMedicalProduct && category1.q04.checkYn === '') ||
        (category1.q04.checkYn === 'Y' &&
          category1.q04.hospitalization === '' &&
          category1.q04.operation === '' &&
          category1.q04.treatment7 === '' &&
          category1.q04.dosing30 === '')
      ) {
        const targetY = document
          .getElementById('q04')
          .getBoundingClientRect().top;

        document.getElementById('notificationScroll').scrollTop =
          currentScrollTop + (targetY - scrollWrapY);
      } else if (
        (!isMedicalProduct && category1.q05.checkYn === '') ||
        (category1.q05.checkYn === 'Y' &&
          category1.q05.cancer !== 'Y' &&
          category1.q05.leukemia !== 'Y' &&
          category1.q05.highBloodPressure !== 'Y' &&
          category1.q05.hypophobia !== 'Y' &&
          category1.q05.myocardial !== 'Y' &&
          category1.q05.cardiomyopathy !== 'Y' &&
          category1.q05.hepaticSclerosis !== 'Y' &&
          category1.q05.stroke !== 'Y' &&
          category1.q05.diabetes !== 'Y' &&
          category1.q05.aids !== 'Y') ||
        (category1.q05.checkYn === 'Y' &&
          category1.q05.diagnosis !== 'Y' &&
          category1.q05.treatment !== 'Y' &&
          category1.q05.hospitalization !== 'Y' &&
          category1.q05.operation !== 'Y' &&
          category1.q05.dosing !== 'Y')
      ) {
        const targetY = document
          .getElementById('q05')
          .getBoundingClientRect().top;

        document.getElementById('notificationScroll').scrollTop =
          currentScrollTop + (targetY - scrollWrapY);
      } else if (
        !isMedicalProduct &&
        genderLabel === '여' &&
        isAddNoticeQ6 &&
        (category1?.q06?.checkYn === '' ||
          (category1?.q06?.checkYn === 'Y' &&
            category1?.q06?.pregnancyPeriod === ''))
      ) {
        const targetY = document
          .getElementById('q06')
          .getBoundingClientRect().top;

        document.getElementById('notificationScroll').scrollTop =
          currentScrollTop + (targetY - scrollWrapY);
      } else {
        // 병력정보 입력체크
        let targetY;
        for (const index of category1.q07.keys()) {
          if (category1.q07[index].diagnosis === '') {
            targetY = document
              .getElementById(`diagnosis${index}`)
              .getBoundingClientRect().top;
            break;
          } else if (
            category1.q07[index].treatStrtDate === '' ||
            category1.q07[index].treatEndDate === ''
          ) {
            targetY = document
              .getElementById(`treatDate${index}`)
              .getBoundingClientRect().top;
            break;
          } else if (
            category1.q07[index].hospitalization !== '1' &&
            category1.q07[index].operation !== '1' &&
            category1.q07[index].outpatient !== '1'
          ) {
            targetY = document
              .getElementById(`treatCheck${index}`)
              .getBoundingClientRect().top;
            break;
          } else if (
            !category1.q07[index].treatArea.left &&
            !category1.q07[index].treatArea.right &&
            !category1.q07[index].treatArea.na
          ) {
            targetY = document
              .getElementById(`treatArea${index}`)
              .getBoundingClientRect().top;
            break;
          } else if (category1.q07[index].treatContent === '') {
            targetY = document
              .getElementById(`treatContent${index}`)
              .getBoundingClientRect().top;

            const targetElement = document.getElementById(
              `treatContent${index}`,
            );
            targetElement.getElementsByTagName('input')[0].focus();
            break;
          } else if (isBeforeRevision && category1.q07[index].hospital === '') {
            targetY = document
              .getElementById(`hospital${index}`)
              .getBoundingClientRect().top;

            const targetElement = document.getElementById(`hospital${index}`);
            targetElement.getElementsByTagName('input')[0].focus();
            break;
          } else if (category1.q07[index].reocur === '') {
            targetY = document
              .getElementById(`reocur${index}`)
              .getBoundingClientRect().top;

            break;
          } else if (category1.q07[index].completeCure === '') {
            targetY = document
              .getElementById(`completeCure${index}`)
              .getBoundingClientRect().top;

            break;
          }
        }

        if (targetY) {
          document.getElementById('notificationScroll').scrollTop =
            currentScrollTop + (targetY - scrollWrapY);
        }
      }
    }
  };

  const handleOnClick = () => {
    // 하단 다음 버튼
    if (isDisabled) {
      setShowErrorCheckMsg(true);
      validationButton(category1);
      validationCheckBox(category1);
      validationScrollMove();
    } else {
      onNext();
    }
  };

  return (
    <React.Fragment>
      {/* 1번 문항 */}
      {!isMedicalProduct ? (
        <>
          {isDementiaProduct ? (
            <>
              {/*치매보험 계약전 알릴의무(질병)*/}
              <div className="infor-list" id="q01">
                <SatQuestionArea
                  title={() => (
                    <p>
                      1. <strong>최근 3개월</strong> 이내에 치매, 알츠하이머,
                      파킨슨병, 뇌졸중증, 외상성뇌손상, 기억상실, 인지능력 상실,
                      노쇠{'(노령으로 인한 정신장애)'}, 언어장애, 행동장애,
                      마비증세로 의사로부터 진찰 또는 검사{'(건강검진 포함)'}를
                      통하여 다음과 같은 의료 행위를 받은 사실이 있습니까?
                      {/* Track2 기획상 아래 텍스트 삭제  */}
                      {/* {!isEasyProduct && '(있다면 세부항목을 선택해주세요.)'} */}
                    </p>
                  )}
                  selectValue={category1.q01.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q1'}
                  onClick={value => handleButtonYnClick('q01', value)}
                  error={
                    showErrorCheckMsg && errorCheckButton.includes('q01')
                      ? '3개월 이내 의료행위를 받은 사실여부를 선택해주세요.'
                      : errorCheckSubData.includes('q01') &&
                        '3개월 이내에 받은 의료행위를 선택해 주세요.'
                  }
                />
                <div className="check-box">
                  {mediKindsFirst.map((option, index) => {
                    return (
                      <React.Fragment key={`mediKindsFirst_${index}`}>
                        {option.tooltip ? (
                          <div className="box-item-tootip">
                            <SatCheckBox
                              value={[
                                category1.q01[option.value] === 'Y'
                                  ? true
                                  : false,
                              ]}
                              options={[
                                { label: option.label, value: option.value },
                              ]}
                              onChange={value =>
                                handleCheckYnClick('q01', option.value, value)
                              }
                            />

                            <SatToolTip
                              show={selectedOption === option.value}
                              setShow={flag =>
                                setSelectedOption(flag ? option.value : '')
                              }
                              toolTips={[
                                {
                                  title: toolTips[option.value].title,
                                  message: () => (
                                    <p>{toolTips[option.value].message}</p>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        ) : (
                          <SatCheckBox
                            value={[
                              category1.q01[option.value] === 'Y'
                                ? true
                                : false,
                            ]}
                            options={[
                              { label: option.label, value: option.value },
                            ]}
                            onChange={value =>
                              handleCheckYnClick('q01', option.value, value)
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* 2번 문항 */}
              <div className="infor-list" id="q02">
                <SatQuestionArea
                  title={() => (
                    <p>
                      2. <strong>최근 5년</strong> 이내에 기억상실,
                      인지능력상실, 노쇠{`(노령으로 인한 정신장애)`}, 언어장애,
                      언어장애, 행동장애, 마비증세와 같은 정신장애 또는
                      신경장애로 의사로부터 진찰 또는 검사를 통하여 다음과 같은
                      의료 행위를 받은 사실이 있습니까?
                    </p>
                  )}
                  selectValue={category1.q02.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q2'}
                  onClick={value => handleButtonYnClick('q02', value)}
                  error={
                    showErrorCheckMsg && errorCheckButton.includes('q02')
                      ? '5년 이내 의료행위를 받은 사실여부를 선택해주세요.'
                      : errorCheckSubData.includes('q02') &&
                        '5년 이내에 받은 의료행위를 선택해 주세요.'
                  }
                />
                <div className="check-box">
                  {mediKindsSecond.map((option, index) => {
                    return (
                      <React.Fragment key={`mediKindsSecond_${index}`}>
                        {option.tooltip ? (
                          <div className="box-item-tootip">
                            <SatCheckBox
                              value={[
                                category1.q02[option.value] === 'Y'
                                  ? true
                                  : false,
                              ]}
                              options={[
                                {
                                  label: option.label,
                                  value: option.value,
                                },
                              ]}
                              onChange={value =>
                                handleCheckYnClick('q02', option.value, value)
                              }
                            />

                            <SatToolTip
                              show={selectedOption === option.value}
                              setShow={flag =>
                                setSelectedOption(flag ? option.value : '')
                              }
                              toolTips={[
                                {
                                  title: toolTips[option.value].title,
                                  message: () => (
                                    <p>{toolTips[option.value].message}</p>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        ) : (
                          <SatCheckBox
                            value={[
                              category1.q02[option.value] === 'Y'
                                ? true
                                : false,
                            ]}
                            options={[
                              { label: option.label, value: option.value },
                            ]}
                            onChange={value =>
                              handleCheckYnClick('q02', option.value, value)
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* 3번 문항 */}
              <div className="infor-list" id="q03">
                <SatQuestionArea
                  title={() => (
                    <p>
                      3. <strong>최근 5년</strong> 이내에 아래 5대 질병으로
                      의사로부터 진찰 또는 검사를 통하여 다음과 같은 의료행위를
                      받은 사실이 있습니까?
                      {/* Track2 기획상 아래 텍스트 삭제  */}
                      {/* (있다면 세부항목을 선택해주세요.) */}
                    </p>
                  )}
                  selectValue={category1.q03.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q3'}
                  onClick={value => handleButtonYnClick('q03', value)}
                  error={
                    showErrorCheckMsg && errorCheckButton.includes('q03')
                      ? '5년 이내 의료행위를 받은 사실 여부를 선택해 주세요. '
                      : errorCheckSubData.includes('q03') &&
                        '5년 이내 5대 질병 진찰/검사여부에 대한 답변을 선택해 주세요.'
                  }
                />

                <div className="check-box-bg">
                  <strong className="text-con">5대 질병</strong>
                  {dementiaThird.bestDisease.map((option, index) => {
                    return (
                      <React.Fragment key={`dementiaThird_${index}`}>
                        <SatCheckBox
                          value={[
                            category1.q03[option.value] === 'Y' ? true : false,
                          ]}
                          options={[
                            { label: option.label, value: option.value },
                          ]}
                          onChange={value =>
                            handleCheckYnClick('q03', option.value, value)
                          }
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="check-box">
                  {dementiaThird.checkList.map((option, index) => {
                    return (
                      <React.Fragment key={`dementiaThird_${index}`}>
                        <SatCheckBox
                          value={[
                            category1.q03[option.value] === 'Y' ? true : false,
                          ]}
                          options={[
                            { label: option.label, value: option.value },
                          ]}
                          onChange={value =>
                            handleCheckYnClick('q03', option.value, value)
                          }
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* 4번 문항 */}
              <div className="infor-list" id="q04">
                <SatQuestionArea
                  title={() => (
                    <p>
                      4. 현재 노인 장기요양보험에 의한 장기요양급여 수급자이거나
                      장기요양인정 심의중입니까?
                    </p>
                  )}
                  selectValue={category1?.q04}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q4'}
                  onClick={value => handleButtonYnClick('q04', value)}
                  error={
                    showErrorCheckMsg &&
                    errorCheckButton.includes('q04') &&
                    '현재 장기요양급여 수급자 여부를 선택해 주세요.'
                  }
                />
              </div>
            </>
          ) : (
            <>
              <div className="infor-list" id="q01">
                <SatQuestionArea
                  title={() => (
                    <p>
                      1. <strong>최근 3개월</strong> 이내 의사로부터 진찰 또는
                      검사(건강검진 포함)를 통하여 다음과 같은 의료행위를 받은
                      사실이 있으신가요?
                      {/* Track2 기획상 아래 텍스트 삭제  */}
                      {/* {!isEasyProduct && '(있다면 세부항목을 선택해주세요.)'} */}
                    </p>
                  )}
                  selectValue={category1.q01.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q1'}
                  onClick={value => handleButtonYnClick('q01', value)}
                  error={
                    showErrorCheckMsg && errorCheckButton.includes('q01')
                      ? '3개월 이내 의료행위를 받은 사실여부를 선택해주세요.'
                      : errorCheckSubData.includes('q01') &&
                        '3개월 이내에 받은 의료행위를 선택해 주세요.'
                  }
                />
                <div className="check-box">
                  {mediKindsFirst.map((option, index) => {
                    return (
                      <React.Fragment key={`mediKindsFirst_${index}`}>
                        {option.tooltip ? (
                          <div className="box-item-tootip">
                            <SatCheckBox
                              value={[
                                category1.q01[option.value] === 'Y' ? true : false,
                              ]}
                              options={[
                                { label: option.label, value: option.value },
                              ]}
                              onChange={value =>
                                handleCheckYnClick('q01', option.value, value)
                              }
                            />

                            <SatToolTip
                              show={selectedOption === option.value}
                              setShow={flag =>
                                setSelectedOption(flag ? option.value : '')
                              }
                              toolTips={[
                                {
                                  title: toolTips[option.value].title,
                                  message: () => (
                                    <p>{toolTips[option.value].message}</p>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        ) : (
                          <SatCheckBox
                            value={[
                              category1.q01[option.value] === 'Y' ? true : false,
                            ]}
                            options={[{ label: option.label, value: option.value }]}
                            onChange={value =>
                              handleCheckYnClick('q01', option.value, value)
                            }
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* 2번 문항 */}
              <div className="infor-list" id="q02">
                {!isEasyProduct ? (
                  <>
                    <SatQuestionArea
                      title={() => (
                        <div className="box-item-tootip">
                          2. <strong>최근 3개월</strong> 이내 마약을 사용하거나
                          혈압강하제
                          <SatToolTip
                            show={selectedOption === 'bpLowering'}
                            setShow={flag =>
                              setSelectedOption(flag ? 'bpLowering' : '')
                            }
                            toolTips={[
                              {
                                title: toolTips['bpLowering'].title,
                                message: () => (
                                  <p>{toolTips['bpLowering'].message}</p>
                                ),
                              },
                            ]}
                          />
                          ,신경안정제,수면제, 각성제(흥분제)
                          <SatToolTip
                            show={selectedOption === 'uppers'}
                            setShow={flag =>
                              setSelectedOption(flag ? 'uppers' : '')
                            }
                            toolTips={[
                              {
                                title: toolTips['uppers'].title,
                                message: () => <p>{toolTips['uppers'].message}</p>,
                              },
                            ]}
                          />
                          , 진통제 등 약물을 상시 복용한 사실이 있으신가요?
                        </div>
                      )}
                      selectValue={category1.q02}
                      options={[
                        { label: '예', value: 'Y' },
                        { label: '아니요', value: 'N' },
                      ]}
                      useType={'btn'}
                      name={'category1_q2'}
                      onClick={value => handleButtonYnClick('q02', value)}
                      error={
                        showErrorCheckMsg &&
                        errorCheckButton.includes('q02') &&
                        '3개월 이내 약물 상시 복용여부를 선택해 주세요.'
                      }
                    />
                  </>
                ) : (
                  <>
                    <SatQuestionArea
                      title={() => (
                        <p>
                          2. <strong>최근 {isFreePickProduct ? 3 : 2}년</strong>{' '}
                          이내에 질병이나 사고로 인하여 다음과 같은 의료행위를 받은
                          사실이 있으신가요?
                        </p>
                      )}
                      selectValue={category1.q02.checkYn}
                      options={[
                        { label: '예', value: 'Y' },
                        { label: '아니요', value: 'N' },
                      ]}
                      useType={'btn'}
                      name={'category1_q2'}
                      onClick={value => handleButtonYnClick('q02', value)}
                      error={
                        showErrorCheckMsg && errorCheckButton.includes('q02')
                          ? '2년 이내 의료행위를 받은 사실여부를 선택해주세요.'
                          : errorCheckSubData.includes('q02') &&
                            '2년 이내에 받은 의료행위를 선택해 주세요.'
                      }
                    />
                    <div className="check-box">
                      {mediKindsSecond.map((option, index) => {
                        return (
                          <React.Fragment key={`mediKindsSecond_${index}`}>
                            {option.tooltip ? (
                              <div className="box-item-tootip">
                                <SatCheckBox
                                  value={[
                                    category1.q02[option.value] === 'Y'
                                      ? true
                                      : false,
                                  ]}
                                  options={[
                                    { label: option.label, value: option.value },
                                  ]}
                                  onChange={value =>
                                    handleCheckYnClick('q02', option.value, value)
                                  }
                                />

                                <SatToolTip
                                  show={selectedOption === option.value}
                                  setShow={flag =>
                                    setSelectedOption(flag ? option.value : '')
                                  }
                                  toolTips={[
                                    {
                                      title: toolTips[option.value].title,
                                      message: () => (
                                        <p>{toolTips[option.value].message}</p>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                            ) : (
                              <SatCheckBox
                                value={[
                                  category1.q02[option.value] === 'Y'
                                    ? true
                                    : false,
                                ]}
                                options={[
                                  { label: option.label, value: option.value },
                                ]}
                                onChange={value =>
                                  handleCheckYnClick('q02', option.value, value)
                                }
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* 3번 문항 */}
              <div className="infor-list" id="q03">
                <SatQuestionArea
                  title={() =>
                    !isEasyProduct ? (
                      <p>
                        3. <strong>최근 1년</strong> 이내에 의사로부터 진찰 또는
                        검사를 받고, 이를 통하여 추가검사(재검사)
                        <SatToolTip
                          show={selectedOption === 'requireAddCheck'}
                          setShow={flag =>
                            setSelectedOption(flag ? 'requireAddCheck' : '')
                          }
                          toolTips={[
                            {
                              title: toolTips['requireAddCheck'].title,
                              message: () => (
                                <p>{toolTips['requireAddCheck'].message}</p>
                              ),
                            },
                          ]}
                        />
                        를 받은 사실이
                        있으신가요?
                      </p>
                    ) : (
                      <div className="box-item-tootip">
                        {!(isFreePickProduct && isSecondMajorRider) ? (
                          /* 일반 간편 상품인 경우 [내맘대로 Pick 상품이면서 2대질병 특약이 없는 경우] */
                          <p>
                            3. <strong>최근 5년</strong> 이내에 의사로부터 진찰 또는
                            검사를 받고, 이를 통하여 암
                            <SatToolTip
                              show={selectedOption === 'cancer'}
                              setShow={flag =>
                                setSelectedOption(flag ? 'cancer' : '')
                              }
                              toolTips={[
                                {
                                  title: toolTips['cancer'].title,
                                  message: () => (
                                    <p>{toolTips['cancer'].message}</p>
                                  ),
                                },
                              ]}
                            />
                            으로 “진단” 받거나 암으로 “입원 또는 수술”을 받은 적이
                            있으신가요?
                          </p>
                        ) : (
                          /* 내맘대로 Pick 상품이면서 2대질병 특약이 있는 경우 */
                          <p>
                            3. <strong>최근 5년</strong> 이내에 의사로부터 진찰 또는
                            검사를 받고, 이를 통하여 암
                            <SatToolTip
                              show={selectedOption === 'cancer'}
                              setShow={flag =>
                                setSelectedOption(flag ? 'cancer' : '')
                              }
                              toolTips={[
                                {
                                  title: toolTips['cancer'].title,
                                  message: () => (
                                    <p>{toolTips['cancer'].message}</p>
                                  ),
                                },
                              ]}
                            />
                            심근경색증, 뇌졸중증(뇌출혈, 뇌경색증)으로 “진단” 받거나
                            암으로 “입원 또는 수술”을 받은 적이 있으신가요?
                          </p>
                        )}
                      </div>
                    )
                  }
                  selectValue={category1.q03}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category1_q3'}
                  onClick={value => handleButtonYnClick('q03', value)}
                  error={
                    showErrorCheckMsg &&
                    errorCheckButton.includes('q03') &&
                    (!isEasyProduct
                      ? '1년 이내 추가검사(재검사) 사실 여부를 선택해 주세요.'
                      : '5년 이내 암 진단 사실 여부를 선택해 주세요.')
                  }
                />
              </div>

              {!isEasyProduct && (
                <>
                  {/* 4번 문항 */}
                  <div className="infor-list" id="q04">
                    <SatQuestionArea
                      title={() => (
                        <p>
                          4. <strong>최근 5년</strong> 이내에 의사로부터 진찰 또는
                          검사를 통하여 다음과 같은 의료행위를 받은 사실이
                          있으신가요?
                          {/* Track2 기획상 아래 텍스트 삭제  */}
                          {/* (있다면 세부항목을 선택해주세요.) */}
                        </p>
                      )}
                      selectValue={category1.q04.checkYn}
                      options={[
                        { label: '예', value: 'Y' },
                        { label: '아니요', value: 'N' },
                      ]}
                      useType={'btn'}
                      name={'category1_q4'}
                      onClick={value => handleButtonYnClick('q04', value)}
                      error={
                        showErrorCheckMsg && errorCheckButton.includes('q04')
                          ? '5년 이내 의료행위를 받은 사실여부를 선택해 주세요.'
                          : errorCheckSubData.includes('q04') &&
                            '5년 이내 받은 의료행위를 선택해 주세요.'
                      }
                    />
                    <div className="check-box">
                      {mediKindsFourth.map((option, index) => {
                        return (
                          <React.Fragment key={`mediKindsFourth_${index}`}>
                            {option.tooltip ? (
                              <div className="box-item-tootip">
                                <SatCheckBox
                                  value={[
                                    category1.q04[option.value] === 'Y'
                                      ? true
                                      : false,
                                  ]}
                                  options={[
                                    { label: option.label, value: option.value },
                                  ]}
                                  onChange={value =>
                                    handleCheckYnClick('q04', option.value, value)
                                  }
                                />

                                <SatToolTip
                                  show={selectedOption === option.value}
                                  setShow={flag =>
                                    setSelectedOption(flag ? option.value : '')
                                  }
                                  toolTips={[
                                    {
                                      title: toolTips['continue'].title,
                                      message: () => (
                                        <p>{toolTips['continue'].message}</p>
                                      ),
                                    },
                                  ]}
                                />
                              </div>
                            ) : (
                              <SatCheckBox
                                value={[
                                  category1.q04[option.value] === 'Y'
                                    ? true
                                    : false,
                                ]}
                                options={[
                                  { label: option.label, value: option.value },
                                ]}
                                onChange={value =>
                                  handleCheckYnClick('q04', option.value, value)
                                }
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {/* 5번 문항 */}
                  <div className="infor-list" id="q05">
                    <SatQuestionArea
                      title={() => (
                        <p>
                          5. <strong>최근 5년</strong> 이내에 아래 10대 질병으로
                          의사로부터 진찰 또는 검사를 통하여 다음과 같은 의료행위를
                          받은 사실이 있으신가요?
                          {/* Track2 기획상 아래 텍스트 삭제  */}
                          {/* (있다면 세부항목을 선택해주세요.) */}
                        </p>
                      )}
                      selectValue={category1.q05.checkYn}
                      options={[
                        { label: '예', value: 'Y' },
                        { label: '아니요', value: 'N' },
                      ]}
                      useType={'btn'}
                      name={'category1_q5'}
                      onClick={value => handleButtonYnClick('q05', value)}
                      error={
                        showErrorCheckMsg && errorCheckButton.includes('q05')
                          ? '5년 이내 의료행위를 받은 사실 여부를 선택해 주세요. '
                          : errorCheckSubData.includes('q05') &&
                            '5년 이내 10대 질병 진찰/검사여부에 대한 답변을 선택해 주세요.'
                      }
                    />

                    <div className="check-box-bg">
                      <strong className="text-con">10대 질병</strong>
                      {mediKindsFifth.bestDisease.map((option, index) => {
                        return (
                          <React.Fragment key={`mediKindsFifth_${index}`}>
                            <SatCheckBox
                              value={[
                                category1.q05[option.value] === 'Y' ? true : false,
                              ]}
                              options={[
                                { label: option.label, value: option.value },
                              ]}
                              onChange={value =>
                                handleCheckYnClick('q05', option.value, value)
                              }
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="check-box">
                      {mediKindsFifth.checkList.map((option, index) => {
                        return (
                          <React.Fragment key={`mediKindsFifth_${index}`}>
                            <SatCheckBox
                              value={[
                                category1.q05[option.value] === 'Y' ? true : false,
                              ]}
                              options={[
                                { label: option.label, value: option.value },
                              ]}
                              onChange={value =>
                                handleCheckYnClick('q05', option.value, value)
                              }
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {/* 6번 문항 */}
                  {genderLabel === '여' && isAddNoticeQ6 && (
                    <div className="infor-list" id="q06">
                      <SatQuestionArea
                        title={() => (
                          <p>
                            [추가] (여성의 경우, 입원 및 수술을 보장하는 경우)현재
                            임신중에 있나요?
                          </p>
                        )}
                        selectValue={category1?.q06?.checkYn}
                        options={[
                          { label: '예', value: 'Y' },
                          { label: '아니요', value: 'N' },
                        ]}
                        useType={'btn'}
                        name={'category1_q6'}
                        onClick={value => handleButtonYnClick('q06', value)}
                        error={
                          showErrorCheckMsg && errorCheckButton.includes('q06')
                            ? '현재 임신 여부를 선택해 주세요.'
                            : errorCheckSubData.includes('q06') &&
                              '임신 개월 수를 입력해 주세요.'
                        }
                      />
                      <div className="box">
                        <span className="dir">약</span>
                        <SatSelect
                          value={category1?.q06?.pregnancyPeriod}
                          title="임신 개월 수"
                          initOption={{ label: '선택', value: '' }}
                          options={pregnantMonthList}
                          onChange={handleSelectPregnant}
                          customStyleClass="w-200"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <div className="infor-list" id="q01">
          <SatQuestionArea
            title={() => (
              <p>
                1. <strong>최근 1년</strong> 이내에 정신질환에 대해서 의사로부터
                진찰 또는 검사를 통하여 다음과 같은 의료행위를 받은 사실이
                있으신가요? (있다면 세부항목을 선택해주세요.)
              </p>
            )}
            selectValue={category1.q01.checkYn}
            options={[
              { label: '예', value: 'Y' },
              { label: '아니요', value: 'N' },
            ]}
            useType={'btn'}
            name={'category1_q1'}
            onClick={value => handleButtonYnClick('q01', value)}
            error={
              showErrorCheckMsg && errorCheckButton.includes('q01')
                ? '1년 이내 의료행위를 받은 사실여부를 선택해주세요.'
                : errorCheckSubData.includes('q01') &&
                  '1년 이내에 받은 의료행위를 선택해 주세요.'
            }
          />
          <div className="check-box">
            {mediKindsFirst.map((option, index) => {
              return (
                <React.Fragment key={`mediKindsFirst_${index}`}>
                  {option.tooltip ? (
                    <div className="box-item-tootip">
                      <SatCheckBox
                        value={[
                          category1.q01[option.value] === 'Y' ? true : false,
                        ]}
                        options={[{ label: option.label, value: option.value }]}
                        onChange={value =>
                          handleCheckYnClick('q01', option.value, value)
                        }
                      />

                      <SatToolTip
                        show={selectedOption === option.value}
                        setShow={flag =>
                          setSelectedOption(flag ? option.value : '')
                        }
                        toolTips={[
                          {
                            title: toolTips[option.value].title,
                            message: () => (
                              <p>{toolTips[option.value].message}</p>
                            ),
                          },
                        ]}
                      />
                    </div>
                  ) : (
                    <SatCheckBox
                      value={[
                        category1.q01[option.value] === 'Y' ? true : false,
                      ]}
                      options={[{ label: option.label, value: option.value }]}
                      onChange={value =>
                        handleCheckYnClick('q01', option.value, value)
                      }
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* 병력 정보 */}
      {showDiseaseHistory &&
        category1.q07.length > 0 &&
        category1.q07.map((data, index) => {
          return (
            <React.Fragment key={`DiseaseHistory_${index}`}>
              <DiseaseHistory
                historyData={data}
                treatStrtDate={data.treatStrtDate}
                treatEndDate={data.treatEndDate}
                index={index}
                totalLength={category1.q07.length}
                onChangeCheckBox={handleDiseaseHistoryCheckBox}
                onDelete={handleDiseaseHistoryDelete}
                addDiseaseHistory={addDiseaseHistory}
                showErrorCheckMsg={showErrorCheckMsg}
                isBeforeRevision={isBeforeRevision}
              />
            </React.Fragment>
          );
        })}
      {/* 추가 고지사항 */}
      {showDiseaseHistory && category1.q07.length === 3 && (
        <>
          <div className="infor-list">
            <div className="infor-txt">
              <div className="box-item-tootip">
                <SatCheckBox
                  value={[
                    category1.q07_2.additionalDisease === 'Y' ? true : false,
                  ]}
                  options={[{ label: '추가 고지사항 있음', value: false }]}
                  onChange={value => handleAdditionalNotice(value)}
                />
                <SatToolTip
                  show={selectedOption === 'addNotice'}
                  setShow={flag => setSelectedOption(flag ? 'addNotice' : '')}
                  toolTips={[
                    {
                      title: toolTips['addNotice'].title,
                      message: () => <p>{toolTips['addNotice'].message}</p>,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </>
      )}
      {/* 추가신청 */}
      {showAddSubscription && !isEasyProduct && (
        <AddSubscription
          specific={category1.q08 && category1.q08.specific}
          decrement={category1.q08 && category1.q08.decrement}
          onChangeCheckBox={handleAddSubscriptionCheckBox}
          isSpecificConstant={isSpecificConstant}
        />
      )}

      {/* 다음 버튼 영역 */}
      <div className="infor-list">
        <div className="btn-area">
          <div className="box-btns-center">
            <SatButton
              styleClass={`btn-s blue btn-wide ${isDisabled ? 'disabled' : ''}`}
              label="다음"
              onClick={handleOnClick}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DiseaseInfo;
